import { useEffect, useState, useContext, createContext, useRef } from 'react';
import { initializeSocket, emitRefreshTournamentRanking, setRefreshTournamentRankingListener, setTradingUpdateListener, disconnectSocket, getPriceBySelectedCoin, getAllCoinsPrices, requestTradingData, setTournamentFinishedListener, setTournamentStatusChangedListener, removeTournamentStatusChangedListener, setOpponentJoinedListener, removeOpponentJoinedListener, setMarginActionRejectedListener, removeMarginActionRejectedListener } from "api/socketService";
import { useTournament } from 'context/TournamentContext';
import { useParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import { getTournamentRanking } from 'api/tournamentService';
import { resolveSelectedCoin } from 'utils/resolveSelectedCoin';


const SocketContext = createContext();

export const useSocket = () => {
  return useContext(SocketContext);
};

const getShortName = (coin) => {
  if (typeof coin === 'string' || coin instanceof String) {
    return coin.split('/')[0].toLowerCase();
  }
  if (coin?.name) {
    return coin?.name.split('/')[0].toLowerCase();
  }
  if (coin?.attributes) {
    return coin?.attributes?.base?.data?.attributes?.short_name.toLowerCase();
  }
  return coin?.base?.data?.attributes?.short_name.toLowerCase();
};

// Normalise tournament ranking data into RankingEntry[] format.
// Backend now returns unified { rank, username, totalInUSDT, totalInUSDTFormatted }[]
// from all sources (REST + WebSocket events).
const normalizeTournamentRanking = (data) => {
  if (!data) {
    return [];
  }

  if (Array.isArray(data)) {
    return data;
  }

  return [];
};

export const SocketProvider = ({ children }) => {
  const { id } = useParams();

  const username = localStorage.getItem('username') ?? Cookies.get('username');

  const [allCoins, setAllCoins] = useState();
  const [allCoinsValues, setAllCoinsValues] = useState();

  const [selectedCoin, selectCoin] = useState();
  const [selectedCoinValue, setSelectedCoinValue] = useState();

  const [selectedOHLCVValue, setOHLCVValue] = useState();

  const setCoinsPricesObj = (newData) => {
    setAllCoinsValues(prevState => ({
      ...prevState,
      [newData.key]: newData.data
    }));
  }

  const setCoin = (data) => {
    if (typeof data === 'string' || data instanceof String) {
      selectCoin({'name': `${data}`})
    } else {
      selectCoin(data)
    }
  }

  useEffect(() => {
    if (allCoinsValues && allCoinsValues[getShortName(selectedCoin)]) {
      setSelectedCoinValue(allCoinsValues[getShortName(selectedCoin)])
    } else {
      setSelectedCoinValue(0)
    }

    if (allCoinsValues && allCoinsValues[`${getShortName(selectedCoin)}OHLCV`]) {
      const ohlcv = allCoinsValues[`${getShortName(selectedCoin)}OHLCV`];
      setOHLCVValue({ ...ohlcv });
    }

    if (selectedCoin !== undefined) {
      sessionStorage.setItem('selectedCoin', JSON.stringify(selectedCoin));
    }
  }, [selectedCoin])

  useEffect(() => {
    if (allCoinsValues && allCoinsValues[getShortName(selectedCoin)]) {
      setSelectedCoinValue(allCoinsValues[getShortName(selectedCoin)])
    }
    if (allCoinsValues && allCoinsValues[`${getShortName(selectedCoin)}OHLCV`]) {
      const ohlcv = allCoinsValues[`${getShortName(selectedCoin)}OHLCV`];
      setOHLCVValue({ ...ohlcv });
    }
  }, [allCoinsValues])

  useEffect(() => {
    if (allCoins?.length) {
      getAllCoinsPrices(allCoins, setCoinsPricesObj);

      if (sessionStorage.hasOwnProperty("selectedCoin")) {
        const savedVariable = sessionStorage.getItem('selectedCoin');
        setCoin(resolveSelectedCoin(JSON.parse(savedVariable), allCoins));
      } else if (selectedCoin === undefined) {
        setCoin(resolveSelectedCoin(undefined, allCoins));
      }
    }
  }, [allCoins]);

  const [socket, setSocket] = useState(null);
  const socketRef = useRef(null);
  // Tournament ranking as unified RankingEntry[] ({ rank, username, totalInUSDT, totalInUSDTFormatted })
  const [tournamentRanking, setTournamentRanking] = useState([]);
  const [socketConnected, setSocketConnected] = useState(false);
  const { tournamentDetails, refreshTournamentState, applyTournamentStatusChangedEvent } = useTournament();
  const statusRefreshInFlightRef = useRef(false);
  const disconnectHandlerRef = useRef(null);
  const [rank, setRank] = useState(null);

  const [tradingUpdates, setTradingUpdates] = useState([]);

  const rawData = tournamentDetails?.data;
  const attributes = rawData?.attributes ?? rawData?.data ?? rawData;
  const effectiveTournamentId = (rawData && rawData.id) || id;
  let effectiveTradeType = attributes?.trade_type;

  if (typeof effectiveTradeType === 'string') {
    const lower = effectiveTradeType.toLowerCase();
    if (lower === 'margin') {
      effectiveTradeType = 'Margin';
    } else if (lower === 'spot') {
      effectiveTradeType = 'Spot';
    }
  }

  useEffect(() => {
    const initSocket = async () => {
      const token = localStorage.getItem('jwt') ?? Cookies.get('jwt');

      const tournamentId = effectiveTournamentId;
      const tradeType = effectiveTradeType;

      let initializedSocket;
      try {
        initializedSocket = await initializeSocket(token, tournamentId, () => setSocketConnected(true));
      } catch (error) {
        console.error('[SocketProvider][TEAM_007] Failed to initialize socket', error);
        setSocketConnected(false);
        return;
      }

      setSocket(initializedSocket);
      socketRef.current = initializedSocket;

      // TEAM_007: Keep socketConnected accurate across disconnect/reconnect cycles.
      try {
        if (initializedSocket) {
          if (disconnectHandlerRef.current) {
            initializedSocket.off('disconnect', disconnectHandlerRef.current);
          }
          disconnectHandlerRef.current = () => setSocketConnected(false);
          initializedSocket.on('disconnect', disconnectHandlerRef.current);
        }
      } catch (error) {
        console.error('[SocketProvider][TEAM_007] Failed to set disconnect listener', error);
      }

      // TEAM_007: SSOT status via tournamentStatusChanged.
      setTournamentStatusChangedListener((event) => {
        if (typeof applyTournamentStatusChangedEvent === 'function') {
          applyTournamentStatusChangedEvent(event);
        }

        // TEAM_007: Keep tournamentDetails/participants in sync after socket status changes.
        if (!statusRefreshInFlightRef.current && typeof refreshTournamentState === 'function') {
          statusRefreshInFlightRef.current = true;
          refreshTournamentState()
            .catch((error) => {
              console.error('[SocketProvider][TEAM_007] refreshTournamentState after status event failed', error);
            })
            .finally(() => {
              statusRefreshInFlightRef.current = false;
            });
        }
      });

      // 005-socket-reward-pool: Listen for opponentJoined to update reward_pool and participant count.
      // Payload is now an object: { tournamentId, opponentUsername, opponentId, participantCount, rewardPool, timestamp, newStatus? }
      setOpponentJoinedListener((data) => {
        // Apply status change if present
        if (data && typeof data === 'object' && data.newStatus) {
          if (typeof applyTournamentStatusChangedEvent === 'function') {
            applyTournamentStatusChangedEvent({
              tournamentId: data.tournamentId,
              newStatus: data.newStatus,
              timestamp: data.timestamp,
            });
          }
        }

        // Refresh tournament details to get updated reward_pool and participants
        if (!statusRefreshInFlightRef.current && typeof refreshTournamentState === 'function') {
          statusRefreshInFlightRef.current = true;
          refreshTournamentState()
            .catch((error) => {
              console.error('[SocketProvider] refreshTournamentState after opponentJoined failed', error);
            })
            .finally(() => {
              statusRefreshInFlightRef.current = false;
            });
        }
      });

      if (tradeType) {
        emitRefreshTournamentRanking(tradeType);
        setRefreshTournamentRankingListener(tradeType, tournamentId, handleRefreshTournamentRanking);
      }

      try {
        if (tournamentRanking.length === 0) {
          const ranking = await getTournamentRanking(tournamentId);
          setTournamentRanking(normalizeTournamentRanking(ranking));
        }
      } catch (error) {
        console.error('Error fetching initial tournament ranking in SocketProvider:', error);
      }

      if (tradeType) {
        setTradingUpdateListener(tradeType, handleTradingUpdate);

        // TEAM_004: Request initial trading history via ACK for Margin tournaments
        if (tradeType === 'Margin') {
          requestTradingData(tournamentId, 'Margin', handleTradingUpdate);
        }
      }

      // TEAM_004: Listen for backend-driven tournamentFinished event as the
      // single source of truth for tournament end. Filter by current tournamentId
      // and refresh tournament state + ranking when it fires.
      setTournamentFinishedListener((event) => {
        try {
          if (!event || typeof event.tournamentId === 'undefined') {
            return;
          }

          if (String(event.tournamentId) !== String(tournamentId)) {
            // Ignore events for other tournaments
            return;
          }

          // Refresh TournamentContext from backend so tournamentStatus becomes FINISHED
          if (typeof refreshTournamentState === 'function') {
            refreshTournamentState();
          }

          // Update local ranking using finalRanking payload (unified RankingEntry[] format)
          if (Array.isArray(event.finalRanking)) {
            setTournamentRanking(event.finalRanking);
          }
        } catch (error) {
          console.error('[SocketProvider][TEAM_004] Failed to handle tournamentFinished event:', error);
        }
      });

      // Backend rejects user-initiated margin mutations after end_at or when
      // tournament status leaves 'in_progress'. Force a context refresh so the
      // UI re-derives phase and renders FinalizingView/EndPage instead of the
      // trading layout.
      setMarginActionRejectedListener((event) => {
        try {
          if (!event) return;
          if (event.tournamentId != null && String(event.tournamentId) !== String(tournamentId)) {
            return;
          }
          console.warn('[SocketProvider] marginActionRejected', event);
          if (typeof refreshTournamentState === 'function') {
            refreshTournamentState();
          }
        } catch (error) {
          console.error('[SocketProvider] Failed to handle marginActionRejected event:', error);
        }
      });
    };

    initSocket();

    return () => {
      removeTournamentStatusChangedListener();
      removeOpponentJoinedListener();
      removeMarginActionRejectedListener();
      try {
        if (socketRef.current && disconnectHandlerRef.current) {
          socketRef.current.off('disconnect', disconnectHandlerRef.current);
        }
      } catch (error) {
        console.error('[SocketProvider][TEAM_007] Failed to cleanup disconnect listener', error);
      }
      disconnectSocket(effectiveTradeType);
    };
  }, [effectiveTournamentId, effectiveTradeType, refreshTournamentState, applyTournamentStatusChangedEvent]);

  // TEAM_007: Fallback polling when socket is not connected (no auth / reconnect / socket unsupported).
  useEffect(() => {
    if (socketConnected) {
      return;
    }

    if (typeof refreshTournamentState !== 'function') {
      return;
    }

    const intervalId = setInterval(() => {
      refreshTournamentState().catch((error) => {
        console.error('[SocketProvider][TEAM_007] refreshTournamentState fallback failed', error);
      });
    }, 10000);

    return () => {
      clearInterval(intervalId);
    };
  }, [socketConnected, refreshTournamentState]);

  useEffect(() => {
    getRank();
  }, [tournamentRanking])

  const getRank = () => {
    if (!Array.isArray(tournamentRanking)) {
      setRank(null);
      return;
    }

    const userEntry = tournamentRanking.find((entry) => entry.username === username);
    setRank(userEntry ? userEntry.rank : null);
  };

  // Define the handler for refreshTournamentRanking
  const handleRefreshTournamentRanking = (data) => {
    if (Array.isArray(data) && data.length > 0) {
      setTournamentRanking(normalizeTournamentRanking(data));
    }
  };

  // Define the handler for trading updates
  const handleTradingUpdate = (data) => {
    if (data && Object.keys(data).length > 0) {
      setTradingUpdates(data);
    }
  };

  const value = {
    socket,
    tournamentRanking,
    rank,
    tournamentDetails,
    getPriceBySelectedCoin,
    getAllCoinsPrices,
    setAllCoins,
    allCoinsValues,
    setCoin,
    selectedCoin,
    selectedCoinValue,
    selectedOHLCVValue,
    tradingUpdates
  };

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};

export default SocketContext;
